import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from '@mui/material';
import axios, { AxiosError }                     from 'axios';
import { Form, Formik, FormikHelpers }           from 'formik';
import qs                                        from 'qs';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Link, useNavigate }                      from 'react-router-dom';
import styled                                    from 'styled-components';
import { ReactComponent as ImageGroup }          from '../../../images/family-group.svg';
import { useAcceptInvitationRequest }            from '../../modules/Settings/components/Requests/queries';
import { LinkLikeText }                          from '../../shared/components/documents/DocumentPicker';
import { LogoBlue }                              from '../../shared/components/Logo';
import { TermsModal }                            from '../../shared/components/modals/Terms/TermsModal';
import { MuiButton }                             from '../../shared/components/MuiButton';
import { EAuthRoutes }                           from '../../shared/constants/constants';
import { PasswordInputField }                    from '../../shared/formFields/PasswordInputField';
import { TextInputField }                        from '../../shared/formFields/TextInputField';
import { queryParams }                 from '../../shared/functions';
import { AuthResponseKeys, IAuthResponse }       from '../../shared/interfaces/auth';
import { isNative }                              from '../../shared/utils/platform';
import { setAuthorizationToken, setUserDetails } from '../../shared/utils/setAuthorizationToken';
import { subscriptionName }                      from '../../utils/subscription';
import { getAuthToken, isTokenExpired }          from '../../utils/authHelpers';
import { IErrorResponse }                        from '../shared/interfaces';

import {
  AuthField,
  AuthSVGSceneHolder,
  PageHeader,
  PageHeaderText,
  ReverseGridContainer,
  SignInText,
  SignUpContainer,
  Terms,
}                                                from '../shared/styles';
import { signInFormSchema }                      from './validation';
import { Notistack } from "../../shared/components/Notistack/Notistack";

interface ISignUpForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const SignUpText = styled(SignInText)`
  margin-bottom: 18px;
    a:focus{
        border-bottom: 2px solid #4D90F0;
        font-weight: 700;
    }
`;

const TextLink = styled(Link)`
  font-size: 14px;
  font-weight: 700;
    &:focus{
        border-bottom: 2px solid #4D90F0;
    }
`;

const INVALID_CREDENTIALS_ERROR = 'Invalid credentials';

export const SignIn: FC = () => {
  const [ termsModalOpened, setTermsModalOpened ]             = useState(false);
  const [ switchAccountModalOpen, setSwitchAccountModalOpen ] = useState(false);

  const pendingSubmit = useRef<{ email: string; password: string; formikActions: FormikHelpers<ISignUpForm> } | null>(null);

  const { mutate: acceptRequest } = useAcceptInvitationRequest();

  const history = useNavigate();
  const params  = queryParams();

  const displayDebugControls = !!localStorage.getItem('debug');

  const initialValues: ISignUpForm = {
    email           : params?.email?.replace(/ /g, '+') as string,
    password        : '',
    confirmPassword : '',
    fullName        : '',
  };

  useEffect(() => {
    if (params?.familyUserId) {
      acceptRequest(params?.familyUserId as string, {
        onSuccess: () => {
          history({
            pathname : EAuthRoutes.SignIn,
            search   : qs.stringify({ email: params?.email?.replace(/ /g, '+') }),
          });
        },
      });
    }
  }, [ params?.familyUserId ]);

  const performSignIn = (email: string, password: string, formikActions: FormikHelpers<ISignUpForm>) => {
    axios.post<IAuthResponse>('/api/v1/sessions', { email, password })
      .then((response) => {
        const setTokenAndRedirectTo = (route: string) => {
          setUserDetails(response?.data?.data?.user);
          setAuthorizationToken(response.data.data.tokens)
            .then(() => {
              history(route);
            });
        };
        if (response.data.data.key === AuthResponseKeys.CREATE_SUBSCRIPTION || response.data.data.tokens) {
          setTokenAndRedirectTo(EAuthRoutes.SelectFamilyComponent);
        } else if (response.data.data.key === AuthResponseKeys.CREATE_FAMILY) {
          setTokenAndRedirectTo(EAuthRoutes.CreateFamily);
        } else {
          const { familyUserId } = queryParams();
          history({
            pathname : EAuthRoutes.CodeConfirmation,
            search   : qs.stringify({ email, familyUserId }),
          });
          formikActions.setSubmitting(false);
        }
      })
      .catch((err: AxiosError<IErrorResponse>) => {
        const errorDetails = err?.response?.data?.ErrorDetails;
        if (errorDetails) {
          const { Key, Message } = errorDetails;
          const errors = Key === AuthResponseKeys.UNAUTHORIZED
            ? { password: Message, email: Message }
            : { password: INVALID_CREDENTIALS_ERROR, email: INVALID_CREDENTIALS_ERROR };
          formikActions.setErrors(errors);
        } else {
          const errorMsg = "Something went wrong, please try again!";
          formikActions.setFieldError('email', errorMsg);
          Notistack.enqueueSnackbar(errorMsg, "error");
        }
        formikActions.setSubmitting(false);
      });
  };

  const handleSubmit = ({ email, password }: ISignUpForm, formikActions: FormikHelpers<ISignUpForm>) => {
    formikActions.setSubmitting(true);

    const authToken = getAuthToken();
    const isLoggedIn = authToken && !isTokenExpired();

    if (isLoggedIn) {
      pendingSubmit.current = { email, password, formikActions };
      setSwitchAccountModalOpen(true);
      formikActions.setSubmitting(false);
      return;
    }

    performSignIn(email, password, formikActions);
  };

  const handleSwitchAccountConfirm = () => {
    setSwitchAccountModalOpen(false);
    if (!pendingSubmit.current) return;
    const { email, password, formikActions } = pendingSubmit.current;
    pendingSubmit.current = null;
    formikActions.setSubmitting(true);
    performSignIn(email, password, formikActions);
  };

  const handleSwitchAccountCancel = () => {
    setSwitchAccountModalOpen(false);
    pendingSubmit.current = null;
    history(EAuthRoutes.SelectFamilyComponent);
  };

  return (
    <>
      <SignUpContainer maxWidth="md">
        <PageHeader mb="20px">
          <LogoBlue />

          <PageHeaderText centeredXs>
            Sign In
          </PageHeaderText>
        </PageHeader>

        <ReverseGridContainer
          container
          spacing={3}
        >
          <Grid
            item
            sm={5}
            md={6}
            lg={7}
          >
            <AuthSVGSceneHolder>
              <ImageGroup />
            </AuthSVGSceneHolder>
          </Grid>

          <Grid
            item
            sm={7}
            md={6}
            lg={5}
          >
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={signInFormSchema}
              onSubmit={handleSubmit}
            >
              { ({
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form>
                  <Grid
                    container
                    spacing={3}
                  >
                    <Grid
                      item
                      xs={12}
                    >
                      <AuthField
                        label="Email"
                        name={'email'}
                        placeholder="you@example.com"
                        inputProps={{ maxLength: 100 }}
                        autoComplete="username"
                        component={TextInputField}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                    >
                      <AuthField
                        label="Password"
                        name={'password'}
                        placeholder="At least 8 characters"
                        inputProps={{ maxLength: 50 }}
                        autoComplete="current-password"
                        component={PasswordInputField}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                    >
                      <TextLink to={`${ EAuthRoutes.ForgotPassword }`}>Forgot Password</TextLink>
                    </Grid>
                  </Grid>

                  <MuiButton
                    fullWidth
                    type="submit"
                    disabled={isSubmitting}
                    margin="40px 0 0"
                  >
                    Sign In
                  </MuiButton>

                  <Divider />

                  <SignUpText>
                    Don’t have an account? <Link to={EAuthRoutes.SignUp}>Sign Up</Link>
                  </SignUpText>

                  <Terms>
                    <LinkLikeText
                      href={'#'}
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsModalOpened(true);
                      }}
                    >Privacy Policy</LinkLikeText>
                  </Terms>

                  { displayDebugControls && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          isNative && CdvPurchase.store.get(subscriptionName)?.getOffer()?.order();
                        }}
                      >
                        Test Subscription
                      </button>

                      <div>
                        <textarea
                          id="app-store-receipt"
                          style={{ marginTop: '15px', width: '100%', height: '150px' }}
                        />
                      </div>
                    </>
                  ) }
                </Form>
              ) }
            </Formik>
          </Grid>
        </ReverseGridContainer>
      </SignUpContainer>

      <TermsModal
        open={termsModalOpened}
        onClose={() => setTermsModalOpened(false)}
      />

      <Dialog open={switchAccountModalOpen} onClose={handleSwitchAccountCancel}>
        <DialogTitle>Switch Account?</DialogTitle>
        <DialogContent>
          You are already signed in. Signing in with another account will replace your current session. Do you want to continue?
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="outlined" onClick={handleSwitchAccountCancel}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleSwitchAccountConfirm}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
